import { useState } from "react";
import { Briefcase, MapPin, Clock, Send, CheckCircle, Upload, ArrowLeft, Wrench, Users, Shield, TrendingUp } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

const API_URL = "/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/gmy7q88s_Photoroom_20260409_140507.png";

const jobListings = [
  {
    id: "draft-beer-technician",
    title: "Draft Beer Technician",
    subtitle: "Entry Level",
    type: "Full-Time",
    location: "Londonderry, NH / Field",
    pay: "$19/hour starting",
    schedule: "Weekdays, 7:00 AM – 2:00 PM",
    description: "Tibs Taps is hiring an entry-level Draft Beer Technician to join our growing team. This is a great opportunity for someone who enjoys working with their hands, problem-solving on the go, and wants to break into the beer industry without needing prior experience. If you're mechanically inclined, dependable, and enjoy interacting with people, we'll train you to become an expert in draft systems.",
    impact: "As a Draft Beer Technician, you'll play a critical role in ensuring bars, restaurants, and venues serve the highest-quality draft beer possible. Your work directly impacts taste, cleanliness, and customer satisfaction — no two days are exactly the same.",
    responsibilities: [
      "Cleaning and maintaining draft beer lines to industry standards",
      "Inspecting, troubleshooting, and servicing draft beer systems",
      "Working independently at customer locations while representing Tibs Taps professionally",
      "Building positive relationships with bar and restaurant staff",
      "Learning the technical side of draft systems, beer quality, and best practices",
    ],
    qualifications: [
      "No beer industry experience required — we provide paid training",
      "Mechanically inclined with strong technical aptitude",
      "Personable, professional, and customer-focused",
      "Self-motivated and comfortable working independently",
      "Able to maneuver full kegs (up to 165 lbs)",
      "Valid driver's license, reliable vehicle, and good driving record",
    ],
    benefits: [
      "Paid, hands-on training",
      "Paid time off",
      "Company vehicle, tools, and uniforms provided",
      "Consistent weekday schedule with afternoons free",
      "Optional weekend on-call opportunities",
      "Client referral bonuses — earn more by helping grow the business",
      "A supportive team environment with room to grow",
      "A chance to build a long-term skill set in a niche, in-demand industry",
    ],
  },
  {
    id: "installation-technician",
    title: "Draft Beer System Installation Technician",
    subtitle: "Part-Time",
    type: "Part-Time",
    location: "Londonderry, NH / Field",
    pay: "From $45/hour",
    schedule: null,
    description: "Tibs Taps is hiring a part-time Draft Beer System Installation Technician. This is a field-based role for someone who enjoys working with tools, solving problems, and building draft beer systems for a variety of establishments. If you're mechanically inclined, dependable, and comfortable working directly with customers, we'll train you to grow into a skilled draft system installer.",
    impact: "Turn your love for hands-on work into a career in the beer industry.",
    responsibilities: [
      "Install draft beer systems in bars and restaurants",
      "Set up beer lines, towers, faucets, couplers, regulators, and cooler components",
      "Assist with system upgrades and modifications",
      "Use hand and power tools safely",
      "Test systems for proper pressure and temperature",
      "Keep job sites clean and professional",
      "Communicate with bar owners and staff",
      "Drive company vehicles to job sites",
    ],
    qualifications: [
      "Mechanical aptitude (plumbing, HVAC, refrigeration, or general tool use preferred)",
      "Creative mindset and ability to adapt to unique installations",
      "Valid driver's license, clean driving record and reliable vehicle",
      "Professional, friendly customer service skills",
      "Able to lift up to 75 lbs and maneuver full kegs (up to 165 lbs)",
      "Comfortable working in walk-in coolers, under bars, and on ladders",
      "Reliable, punctual, and detail-oriented",
    ],
    preferred: [
      "Construction, installation, or field service experience",
      "Draft beer or beverage system familiarity",
      "Basic understanding of gas systems (CO₂ / nitrogen)",
      "Bar or restaurant industry experience",
    ],
    benefits: [
      "Hourly rate from $45/hr",
      "Paid time off (PTO)",
      "Client referral bonuses",
      "Company vehicle and tools provided",
      "Hands-on training and long-term career opportunity",
      "A supportive team environment with room to grow",
    ],
  },
];

export default function CareersPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [formLoadedAt] = useState(() => Date.now() / 1000);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    message: "",
    website: "",  // honeypot
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!["pdf", "doc", "docx"].includes(ext)) {
        toast.error("Please upload a PDF, DOC, or DOCX file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File must be under 10MB");
        return;
      }
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("position", formData.position);
      data.append("message", formData.message);
      data.append("website", formData.website);
      data.append("form_loaded_at", formLoadedAt);
      if (resumeFile) {
        data.append("resume", resumeFile);
      }
      await axios.post(`${API_URL}/applications`, data);

      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-[#27272A] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="careers-home-link">
            <img src={LOGO_URL} alt="Tibs Taps" className="w-10 h-10" />
            <span className="text-white font-heading text-lg">Tibs Taps</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="overline mb-4">Careers</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Join the <span className="gold-text">Tibs Taps Team</span>
          </h1>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            We're growing and looking for skilled, motivated people to help us deliver 
            the best draft beer service in New England.
          </p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl text-white text-center mb-12">Why Work With Tibs Taps?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Wrench, title: "Hands-On Work", desc: "Every day is different — work with your hands across New England" },
              { icon: Users, title: "Great Team", desc: "Join a tight-knit crew that takes pride in quality service" },
              { icon: TrendingUp, title: "Growth", desc: "Grow with us as we expand across New England" },
              { icon: Shield, title: "Full Training", desc: "We provide paid training and paid time off — no experience needed" },
            ].map((item) => (
              <div key={item.title} className="p-6 bg-[#1A1A1A] border border-[#27272A] rounded-xl text-center">
                <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-white font-medium mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-white text-center mb-12">Open Positions</h2>

          {jobListings.map((job) => (
            <div key={job.id} className="bg-[#1A1A1A] border border-[#27272A] rounded-2xl p-8 mb-8" data-testid={`job-${job.id}`}>
              <div className="flex flex-wrap items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl text-white">{job.title}</h3>
                  {job.subtitle && <p className="text-[#D4AF37] font-medium">{job.subtitle}</p>}
                  <div className="flex flex-wrap gap-4 mt-2">
                    <span className="flex items-center gap-1 text-[#A1A1AA] text-sm">
                      <Clock className="w-4 h-4" /> {job.type}
                    </span>
                    <span className="flex items-center gap-1 text-[#A1A1AA] text-sm">
                      <MapPin className="w-4 h-4" /> {job.location}
                    </span>
                    {job.pay && (
                      <span className="flex items-center gap-1 text-[#D4AF37] text-sm font-medium">
                        {job.pay}
                      </span>
                    )}
                  </div>
                  {job.schedule && (
                    <p className="text-[#A1A1AA] text-sm mt-2">Schedule: {job.schedule}</p>
                  )}
                </div>
              </div>

              <p className="text-[#A1A1AA] mb-4">{job.description}</p>
              {job.impact && <p className="text-[#A1A1AA] mb-6">{job.impact}</p>}

              <div className="space-y-6">
                <div>
                  <h4 className="text-white font-medium mb-3">What You'll Be Doing</h4>
                  <ul className="space-y-2">
                    {job.responsibilities.map((item) => (
                      <li key={item} className="flex items-start text-sm text-[#A1A1AA]">
                        <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mr-3 mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-white font-medium mb-3">Requirements</h4>
                  <ul className="space-y-2">
                    {job.qualifications.map((item) => (
                      <li key={item} className="flex items-start text-sm text-[#A1A1AA]">
                        <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mr-3 mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {job.preferred && (
                  <div>
                    <h4 className="text-white font-medium mb-3">Preferred (Not Required)</h4>
                    <ul className="space-y-2">
                      {job.preferred.map((item) => (
                        <li key={item} className="flex items-start text-sm text-[#A1A1AA]">
                          <span className="w-1.5 h-1.5 bg-[#D4AF37]/50 rounded-full mr-3 mt-2 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <h4 className="text-white font-medium mb-3">What We Offer</h4>
                  <ul className="space-y-2">
                    {job.benefits.map((item) => (
                      <li key={item} className="flex items-start text-sm text-[#A1A1AA]">
                        <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mr-3 mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="text-[#A1A1AA] mt-6 italic border-t border-[#27272A] pt-6">
                If you're energetic, reliable, and looking for a job that combines technical work with independence and real-world impact, Tibs Taps could be the perfect fit for you.
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-[#141414]" id="apply">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-white text-center mb-4">Apply Now</h2>
          <p className="text-[#A1A1AA] text-center mb-12">
            Interested in joining our team? Fill out the form below and attach your resume.
          </p>

          <div className="bg-[#1A1A1A] border border-[#27272A] rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-12" data-testid="application-success">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="font-heading text-2xl text-white mb-2">Application Received!</h3>
                <p className="text-[#A1A1AA] mb-6">
                  Thank you for your interest in joining Tibs Taps. We'll review your application and be in touch soon.
                </p>
                <Link to="/" className="btn-gold inline-flex items-center gap-2 px-6 py-3">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="application-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="app-name" className="text-[#A1A1AA] mb-2 block">Full Name *</Label>
                    <Input
                      id="app-name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="input-dark"
                      data-testid="app-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="app-email" className="text-[#A1A1AA] mb-2 block">Email *</Label>
                    <Input
                      id="app-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="input-dark"
                      data-testid="app-input-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="app-phone" className="text-[#A1A1AA] mb-2 block">Phone *</Label>
                    <Input
                      id="app-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="input-dark"
                      data-testid="app-input-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="app-position" className="text-[#A1A1AA] mb-2 block">Position *</Label>
                    <select
                      id="app-position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-md border border-[#27272A] bg-[#141414] text-white px-3 py-2 text-sm input-dark"
                      data-testid="app-input-position"
                    >
                      <option value="">Select a position...</option>
                      {jobListings.map((job) => (
                        <option key={job.id} value={job.title}>{job.title}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="app-message" className="text-[#A1A1AA] mb-2 block">When are you available to meet with us?</Label>
                  <Textarea
                    id="app-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="input-dark min-h-[120px]"
                    data-testid="app-input-message"
                  />
                </div>

                <div>
                  <Label className="text-[#A1A1AA] mb-2 block">Resume (PDF, DOC, DOCX — max 10MB)</Label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      id="resume-upload"
                      data-testid="app-input-resume"
                    />
                    <label
                      htmlFor="resume-upload"
                      className="flex items-center gap-3 p-4 bg-[#141414] border border-dashed border-[#27272A] rounded-lg cursor-pointer hover:border-[#D4AF37]/50 transition-colors"
                    >
                      <Upload className="w-5 h-5 text-[#A1A1AA]" />
                      <span className="text-[#A1A1AA] text-sm">
                        {resumeFile ? resumeFile.name : "Click to upload your resume"}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Honeypot - hidden from humans */}
                <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="pb-16">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gold w-full flex items-center justify-center space-x-2 disabled:opacity-50 relative z-50"
                    data-testid="app-submit"
                  >
                    {isSubmitting ? (
                      <span>Submitting...</span>
                    ) : (
                      <>
                        <span>Submit Application</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0A0A0A] border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#A1A1AA] text-sm">
            &copy; {new Date().getFullYear()} Tibs Taps. All rights reserved. |{" "}
            <Link to="/" className="text-[#D4AF37] hover:underline">Back to Home</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
